import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "电影分类" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    } else {
      navigate("/search");
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-background-50/95 backdrop-blur transition-colors ${
        scrolled ? "border-background-200" : "border-background-200/50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-fill text-lg w-[18px] h-[18px] flex items-center justify-center" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-bold tracking-tight text-foreground-950">
              蜜桃成熟时
            </span>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.22em] text-primary-600">
              Online Cinema
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={submitSearch} className="relative hidden lg:block">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、导演或类型"
              className="h-9 w-64 rounded-full border border-background-300/70 bg-background-50 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200/70"
            />
          </form>

          <button
            type="button"
            aria-label="打开搜索"
            onClick={() => setSearchOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
          >
            <i className="ri-search-line text-base w-4 h-4 flex items-center justify-center" />
          </button>

          <Link
            to="/category"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            <i className="ri-play-circle-line text-base w-4 h-4 flex items-center justify-center" />
            开始观影
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-800 md:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-lg w-[18px] h-[18px] flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <form onSubmit={submitSearch} className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              type="text"
              name="q"
              value={keyword}
              autoFocus
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、导演或类型"
              className="h-10 w-full rounded-full border border-background-300/70 bg-background-100 pl-9 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-8 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive
                      ? "bg-primary-50 font-medium text-primary-700"
                      : "text-foreground-700"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center text-foreground-400" />
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}